import { useState } from "react";
import { toast } from "sonner";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * SaveSearchButton — auth-gated; opens a dialog to name + persist a search.
 * Caller passes the current `kind` ("listing" | "jv" | "business") and `filters` dict.
 */
export default function SaveSearchButton({ kind, filters, defaultName, variant = "outline", className = "" }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState(defaultName || "");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    if (!user) return null; // hide for anon users

    const submit = async () => {
        if (!name.trim()) {
            toast.error("Give your search a name");
            return;
        }
        setSaving(true);
        try {
            await api.post("/saved-searches", { name: name.trim(), kind, filters: filters || {} });
            toast.success("Saved. Find it on your dashboard.");
            setSaved(true);
            setOpen(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button
                type="button"
                variant={variant}
                onClick={() => { setName(defaultName || ""); setOpen(true); }}
                data-testid="save-search-btn"
                className={`font-app ${className}`}
            >
                {saved ? <BookmarkCheck className="h-4 w-4 mr-2 text-fiji-green" /> : <Bookmark className="h-4 w-4 mr-2" />}
                {saved ? "Saved" : "Save search"}
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="save-search-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display">Save this search</DialogTitle>
                        <DialogDescription className="font-app">
                            Pin these filters to your dashboard so you can re-run them in one click.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Label className="font-app">Search name</Label>
                        <Input
                            data-testid="save-search-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Coral Coast under FJ$500k"
                            autoFocus
                        />
                        <div className="text-xs text-muted-foreground font-app bg-secondary/50 rounded p-2.5">
                            Filters: {Object.keys(filters || {}).length === 0
                                ? <span className="italic">no filters — saves all listings</span>
                                : <span>{Object.entries(filters).map(([k, v]) => `${k}=${v}`).join(" · ")}</span>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)} className="font-app">Cancel</Button>
                        <Button onClick={submit} disabled={saving} data-testid="save-search-confirm" className="bg-ocean-green text-white">
                            {saving ? "Saving…" : "Save search"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
